import {
  CLIENT_EVENTS_BODY_LIMIT,
  CLIENT_EVENTS_FLUSH_MS,
  CLIENT_EVENTS_PER_BATCH,
  CLIENT_EVENTS_PER_PAGE,
  type ClientContext,
  type ClientErrorEvent,
  type ClientEvent,
  type ClientEventBatch,
  reduceClientEvent,
} from "@esi/shared";
import { pushRecentError } from "./recent-errors.ts";

const encoder = new TextEncoder();
const byteLength = (batch: ClientEventBatch) => encoder.encode(JSON.stringify(batch)).length;

/** How a batch leaves: a beacon when the page is hidden (it may be closing), else `fetch`. */
export type SendBatch = (batch: ClientEventBatch, options: { beacon: boolean }) => void;

export interface CollectorOptions {
  send: SendBatch;
  /** The page's coarse context (browser and OS family, locale, viewport class). */
  context: () => ClientContext;
  /** This bundle's build ID, in the envelope for beacons (they cannot send the header). */
  build: string;
  /** The page's origin: frames of other origins read `external`. */
  origin: string;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
}

/**
 * Client telemetry's queue (openspec change `client-telemetry` D2): events are reduced before
 * they are queued, held until `/api/me` (or the signed-out session answer) says whether to
 * report, then sent in batches of at most 20, one batch per 10 s and when the page is hidden,
 * and never more than 100 events per page load. The same error (name, message, first frame)
 * queued again only raises its `count`. Off: the queue is dropped and nothing is sent; reduced
 * errors still reach the tab's recent errors.
 */
export function createCollector(options: CollectorOptions) {
  const setTimer = options.setInterval ?? ((fn, ms) => globalThis.setInterval(fn, ms));
  const clearTimer =
    options.clearInterval ??
    ((handle) => globalThis.clearInterval(handle as ReturnType<typeof setInterval>));
  let state: "pending" | "armed" | "off" = "pending";
  let queue: ClientEvent[] = [];
  /** Queued, unsent errors by their duplicate key. */
  let queuedErrors = new Map<string, ClientErrorEvent>();
  /** Events this page load has taken (queued or sent): at most `CLIENT_EVENTS_PER_PAGE`. */
  let taken = 0;
  let timer: unknown;

  const errorKey = (e: ClientErrorEvent) => {
    const frame = e.frames[0];
    return `${e.name}\n${e.message ?? ""}\n${frame ? `${frame.file}:${frame.line}:${frame.col}` : ""}`;
  };

  function record(event: ClientEvent): void {
    const reduced = reduceClientEvent(event, options.origin);
    if (reduced.kind === "error") {
      pushRecentError({ ...reduced });
    }
    if (state === "off") {
      return;
    }
    if (reduced.kind === "error") {
      const queued = queuedErrors.get(errorKey(reduced));
      if (queued) {
        queued.count = Math.min(queued.count + 1, CLIENT_EVENTS_PER_PAGE);
        return;
      }
    }
    if (taken >= CLIENT_EVENTS_PER_PAGE) {
      return;
    }
    taken += 1;
    queue.push(reduced);
    if (reduced.kind === "error") {
      queuedErrors.set(errorKey(reduced), reduced);
    }
  }

  /** Sends one batch of at most 20 queued events, when armed. */
  function flush(beacon = false): void {
    if (state !== "armed" || queue.length === 0) {
      return;
    }
    const context = options.context();
    // At most 20 events, and under the endpoint's byte limit (UTF-8, envelope included): the
    // rest stays queued for the next flush. One event alone always fits (bounded fields).
    let take = Math.min(queue.length, CLIENT_EVENTS_PER_BATCH);
    while (
      take > 1 &&
      byteLength({ build: options.build, context, events: queue.slice(0, take) }) >
        CLIENT_EVENTS_BODY_LIMIT
    ) {
      take -= 1;
    }
    const events = queue.slice(0, take);
    queue = queue.slice(take);
    for (const event of events) {
      if (event.kind === "error") {
        queuedErrors.delete(errorKey(event));
      }
    }
    try {
      options.send({ build: options.build, context, events }, { beacon });
    } catch {
      // Telemetry never breaks the page, nor reports on itself.
    }
  }

  return {
    record,
    flush,
    /** `telemetry.clientEvents` is on: send what is queued, then every 10 s. */
    arm(): void {
      if (state === "armed") {
        return;
      }
      state = "armed";
      timer = setTimer(() => flush(), CLIENT_EVENTS_FLUSH_MS);
      flush();
    },
    /** It is off: drop the queue, send nothing more. */
    disarm(): void {
      if (timer !== undefined) {
        clearTimer(timer);
        timer = undefined;
      }
      state = "off";
      queue = [];
      queuedErrors = new Map();
    },
    get state() {
      return state;
    },
    /** Queued events (tests). */
    get queued(): readonly ClientEvent[] {
      return queue;
    },
  };
}

export type Collector = ReturnType<typeof createCollector>;
